<template>
  <div class="impersonate-modal">
    <BaseInput
      ref="searchInput"
      class="search"
      :debounce="300"
      has-styles
      placeholder="Search an account by email"
      :prevent-default-on-keys="['enter', 'up', 'down']"
      :value="query"
      @update:value="(v: string) => (query = v)"
      @down="() => move(1)"
      @enter="validate"
      @up="() => move(-1)"
    />

    <div ref="resultsEl" class="results">
      <Spinner v-if="isSearching" class="results-spinner" />
      <div v-else-if="searchError" class="message error">
        {{ searchError }}
      </div>
      <div v-else-if="!query" class="message">
        Type part of an email to search.
      </div>
      <BaseNoResults v-else-if="accounts.length === 0" />
      <div
        v-for="(account, index) in accounts"
        v-else
        :key="account.id"
        class="result"
        :class="{
          highlighted: index === highlighted,
          impersonated: impersonation?.account.id === account.id,
        }"
        @mouseenter="() => (highlighted = index)"
      >
        <BaseButton
          kind="auxiliary"
          is-expanded
          :state="impersonating === account.id ? 'loading' : 'default'"
          :text="account.email"
          @click="() => impersonate(account)"
        />
      </div>
    </div>

    <div v-if="accounts.length > 0 && !isSearching" class="hints">
      <span><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
      <span><kbd>⏎</kbd> impersonate</span>
    </div>

    <div v-if="impersonation" class="token">
      <div class="token-account">
        Token for <strong>{{ impersonation.account.email }}</strong>
      </div>
      <code class="token-value">{{ impersonation.token }}</code>
      <div class="token-help">
        Impersonation is not wired into the running app yet: restart it with
        <code>ROUTINE_TOKEN=&lt;token&gt;</code> in its environment to run as
        this account.
      </div>
      <BaseButton
        kind="main"
        type="secondary"
        text="Copy token"
        @click="copyToken"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick, onMounted, ref, watch } from "vue";

import type { Account, Impersonation } from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import BaseNoResults from "@/components/base/BaseNoResults.vue";
import * as inject from "@/components/inject";

export default defineComponent({
  components: { BaseButton, BaseInput, BaseNoResults },
  setup() {
    const controller = inject.controller();
    const logger = inject.logger("ImpersonateModal");

    const accounts = ref<Account[]>([]);
    const highlighted = ref(0);
    const impersonation = ref<Impersonation | null>(null);
    const impersonating = ref<string | null>(null);
    const isSearching = ref(false);
    const query = ref("");
    const resultsEl = ref<HTMLElement | null>(null);
    const searchError = ref<string | null>(null);
    const searchInput = ref<InstanceType<typeof BaseInput> | null>(null);

    onMounted(() => searchInput.value?.focus());

    // Only the newest search may publish its results: a slow earlier request
    // must not overwrite those of a more recent query.
    let search = 0;

    watch(query, async (value) => {
      const current = ++search;
      searchError.value = null;
      if (!value) {
        accounts.value = [];
        isSearching.value = false;
        return;
      }
      isSearching.value = true;
      try {
        const found = await controller.admin.accounts(value);
        if (current !== search) return;
        accounts.value = found;
        highlighted.value = 0;
      } catch (error) {
        if (current !== search) return;
        accounts.value = [];
        searchError.value = "Search failed. Is this account a Routine admin?";
        logger.error("account search failed", { error });
      } finally {
        if (current === search) isSearching.value = false;
      }
    });

    // Reach the highlighted row through the DOM rather than keeping a
    // template ref per row.
    const revealHighlighted = () =>
      void nextTick(() => {
        const rows = resultsEl.value?.querySelectorAll(".result");
        rows?.[highlighted.value]?.scrollIntoView({ block: "nearest" });
      });

    const move = (delta: number) => {
      const { length } = accounts.value;
      if (length === 0) return;
      highlighted.value = (highlighted.value + delta + length) % length;
      revealHighlighted();
    };

    const impersonate = async (account: Account) => {
      impersonating.value = account.id;
      searchError.value = null;
      try {
        impersonation.value = await controller.admin.impersonate(account.email);
      } catch (error) {
        impersonation.value = null;
        searchError.value = `Impersonating ${account.email} failed.`;
        logger.error("impersonation failed", { error });
      } finally {
        impersonating.value = null;
      }
    };

    const validate = () => {
      const account = accounts.value[highlighted.value];
      if (account) void impersonate(account);
    };

    const copyToken = async () => {
      const token = impersonation.value?.token;
      if (!token) return;
      await copy(token);
      controller.notifications.notify("Token copied to clipboard");
    };

    return {
      accounts,
      copyToken,
      highlighted,
      impersonate,
      impersonating,
      impersonation,
      isSearching,
      move,
      query,
      resultsEl,
      searchError,
      searchInput,
      validate,
    };
  },
});
</script>

<style scoped lang="scss">
.impersonate-modal {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 420px;
}

.results {
  display: flex;
  flex-direction: column;
  min-height: 140px;
  max-height: 240px;
  overflow-y: auto;
  padding: 4px;
  border-radius: $border-radius-m;
  @include themed() {
    border: 1px solid t($border);
  }

  // Once the list overflows, flex would otherwise squeeze every row to a
  // few pixels instead of scrolling.
  > * {
    flex: none;
  }
}

.results-spinner {
  margin: auto;
}

.result {
  border-radius: $border-radius-s;

  &.highlighted {
    @include themed() {
      background-color: t($bg-menu-hover);
    }
  }

  // The account a token was already minted for, so a second pass through the
  // list shows where you have been.
  &.impersonated :deep(.text) {
    font-weight: 500;
    @include themed() {
      color: t($primary);
    }
  }
}

.hints {
  display: flex;
  gap: 12px;
  @extend %font-small-regular;
  @include themed() {
    color: t($grey-30);
  }

  kbd {
    font: inherit;
    margin-inline-end: 2px;
  }
}

.message {
  @extend %font-body-regular;
  padding: 8px 4px;
  @include themed() {
    color: t($grey-40);
  }

  &.error {
    color: $red;
    overflow-wrap: anywhere;
  }
}

.token {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: $border-radius-m;
  @include themed() {
    background-color: t($bg-menu-hover);
  }
}

.token-account {
  @extend %font-body-regular;
  @include themed() {
    color: t($primary);
  }
}

.token-value {
  @extend %font-small-regular;
  font-family: monospace;
  width: 100%;
  padding: 6px 8px;
  border-radius: $border-radius-s;
  word-break: break-all;
  user-select: all;
  @include themed() {
    background-color: t($bg);
    color: t($primary);
  }
}

.token-help {
  @extend %font-small-regular;
  @include themed() {
    color: t($grey-40);
  }
}
</style>
